import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Form, Image, Modal } from "react-bootstrap";
import { FaSignOutAlt, FaImage } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const defaultProfilePicture = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR3Ok7maqK-S4T_ae5SGafk3H66CnppW6d-7g&s";

const ProfilePage = () => {
    const [profilePicture, setProfilePicture] = useState(defaultProfilePicture);
    const [userInfo, setUserInfo] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
    });
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const { t } = useTranslation();

    // ✅ Load user data and cached image
    useEffect(() => {
        const loadUserFromStorage = () => {
            try {
                setLoading(true);

                // Load cached profile picture
                const cachedImage = localStorage.getItem("profilePicture");
                if (cachedImage) {
                    setProfilePicture(cachedImage);
                }

                // Load user data
                const storedUser = localStorage.getItem("user");

                if (storedUser) {
                    const userData = JSON.parse(storedUser);
                    setUserInfo({
                        name: userData.fullName || "",
                        email: userData.email || "",  // ✅ Changed: Only get email field
                        phone: userData.phoneNumber || "",
                        address: userData.address || "",
                    });
                } else {
                    const fullName = localStorage.getItem("userFullName");
                    const email = localStorage.getItem("userEmail");  // ✅ Added: Get email separately
                    const phoneNumber = localStorage.getItem("userPhone");
                    const address = localStorage.getItem("userAddress");

                    if (fullName || email) {
                        setUserInfo({
                            name: fullName || "",
                            email: email || "",  // ✅ Changed: Use email from localStorage
                            phone: phoneNumber || "",
                            address: address || "",
                        });
                    } else {
                        setError("User data not found. Please login again.");
                        setTimeout(() => navigate("/login"), 2000);
                    }
                }
            } catch (error) {
                console.error("Error loading user data:", error);
                setError("Failed to load user data");
            } finally {
                setLoading(false);
            }
        };

        loadUserFromStorage();
    }, [navigate]);

    useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);

    // Additional scroll after a tiny delay to ensure everything is rendered
    const timer = setTimeout(() => {
      // Get the form or cart container
      const formContainer = document.querySelector('.delivery-form') ||
        document.querySelector('form') ||
        document.querySelector('.mt-top-cartpage') ||
        document.querySelector('.cart-items-sidebar');

      if (formContainer) {
        formContainer.scrollTop = 0;

        // Also focus on first input if it's the checkout form
        if (window.location.pathname.includes('checkout')) {
          const firstInput = document.querySelector('input, select, textarea');
          if (firstInput) {
            setTimeout(() => firstInput.focus(), 100);
          }
        }
      }
    }, 50);

    return () => clearTimeout(timer);
  }, []);

    const handleRemoveImage = () => {
        setProfilePicture(defaultProfilePicture);
        localStorage.removeItem("profilePicture"); // remove cached image
        setShowImageModal(false); // close modal
    };

    // ✅ Handle profile picture upload and cache it
    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const imageDataUrl = reader.result;
                setProfilePicture(imageDataUrl);

                // Save to localStorage (acts as cache)
                localStorage.setItem("profilePicture", imageDataUrl);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleShowLogoutModal = () => setShowLogoutModal(true);
    const handleCloseLogoutModal = () => setShowLogoutModal(false);

    const handleConfirmLogout = () => {
        // Clear all user-related data including profile picture
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('user');
        localStorage.removeItem('userFullName');
        localStorage.removeItem('userPhone');
        localStorage.removeItem('userAddress');
        localStorage.removeItem('profilePicture');

        handleCloseLogoutModal();
        navigate('/login', { replace: true });
    };

    const handleShowImageModal = () => setShowImageModal(true);
    const handleCloseImageModal = () => setShowImageModal(false);

    if (loading) {
        return (
            <Container className="my-5 text-center">
                <div>Loading user data...</div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="my-5 text-center">
                <div className="text-danger mb-3">{error}</div>
                <Button variant="primary" onClick={() => window.location.reload()}>
                    Retry
                </Button>
            </Container>
        );
    }

    return (
        <Container className="my-5 rounded-4" style={{ fontFamily: "'Hanuman', Arial, sans-serif" }}>
            <Row className="justify-content-center pf-top">
                <Col md={8}>
                    <Card className="p-3 p-md-5 shadow-custom border-0 rounded-4">
                        <Row>
                            <Col md={4} className="text-center">
                                <div className="position-relative mb-4">
                                    <Image
                                        src={profilePicture}
                                        roundedCircle
                                        fluid
                                        className="border border-2 border-dark"
                                        style={{ width: "160px", height: "160px", cursor: "pointer" }}
                                        onClick={handleShowImageModal}
                                    />
                                    <label htmlFor="profile-picture-upload" className="edit-icon border">
                                        <FaImage
                                            className="position-absolute bottom-0 translate-middle fs-2 p-1 text-dark"
                                            style={{ cursor: "pointer" }}
                                        />
                                    </label>
                                </div>
                                <input
                                    type="file"
                                    id="profile-picture-upload"
                                    style={{ display: "none" }}
                                    onChange={handleProfilePictureChange}
                                    accept="image/*"
                                />
                                <h4 className="mb-2">{userInfo.name}</h4>
                                <p className="text-muted">{userInfo.email}</p>
                                <Button variant="danger" className="w-100 mt-2" onClick={handleShowLogoutModal}>
                                    <FaSignOutAlt className="me-2" />
                                    {t('Logout')}
                                </Button>
                            </Col>
                            <Col md={8}>
                                <h3 className="mt-4 mt-md-0">{t('Profile Information')}</h3>
                                <Form className="border rounded-4 shadow-md p-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('UserName')}</Form.Label>
                                        <Form.Control type="text" className="shadow-none border" value={userInfo.name} readOnly />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('Email')}</Form.Label>
                                        <Form.Control type="text" className="shadow-none border" value={userInfo.email} readOnly />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('PhoneNumber')}</Form.Label>
                                        <Form.Control type="text" className="shadow-none border" value={userInfo.phone} readOnly />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('Address')}</Form.Label>
                                        <Form.Control type="text" className="shadow-none border" value={userInfo.address} readOnly />
                                    </Form.Group>
                                </Form>
                            </Col>
                        </Row>
                    </Card>
                </Col>
            </Row>

            {/* Logout Confirmation Modal */}
            <Modal show={showLogoutModal} onHide={handleCloseLogoutModal}>
                <Modal.Header closeButton className="border-0 mx-3 my-3"></Modal.Header>
                <Modal.Body className="fs-3 text-center">Are you sure you want to logout?</Modal.Body>
                <Modal.Footer className="border-0 my-3 mx-3">
                    <Button variant="secondary" onClick={handleCloseLogoutModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmLogout}>
                        Logout
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Image Modal */}
            <Modal show={showImageModal} onHide={handleCloseImageModal} centered size="md">
                <Modal.Header closeButton className="border-0"></Modal.Header>
                <Modal.Body className="text-center p-4">
                    <Image
                        src={profilePicture}
                        fluid
                        style={{ maxWidth: "100%", height: "auto" }}
                        className="rounded-3"
                    />

                    <Button
                        variant="danger"
                        className="mt-4 px-4"
                        onClick={handleRemoveImage}
                    >
                        {t('Remove Image')}
                    </Button>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default ProfilePage;
